import { useState, type JSX } from "react";

import { DeleteWithConfirmationDialog } from "@impero-com/common/components/deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { ActionSpec, ActionSpecStatus, emitNotification } from "@impero-com/ui";
import { useNavigate } from "react-router-dom";

import deleteIcon from "img/delete.svg";
import { useDeleteEntityMutation } from "org/entities/queries/useDeleteEntityMutation";
import { WebRoute } from "routes";

interface DeleteAction {
    deleteAction: ActionSpec;
    deleteDialogComponent: JSX.Element;
}

export function useDeleteAction(
    hasDeleteAction: boolean,
    controlEntityId: number
): DeleteAction {
    const { t } = useTranslation();
    const { mutateAsync: mutateDelete, isPending: isDeleting } =
        useDeleteEntityMutation();
    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);

    const handleDeleteClick = async () => {
        await mutateDelete({ entityId: controlEntityId });
        navigate(WebRoute.ENTITIES);
        emitNotification("success", t("EntityView_DeleteNotification"));
    };

    const actionButtonStatus: ActionSpecStatus = hasDeleteAction
        ? { type: "ENABLED" }
        : {
              type: "DISABLED",
              reason: t("EntityView_DeleteAction_NoAccess"),
          };

    const deleteAction: ActionSpec = {
        id: "deleteControlEntity",
        icon: { svg: deleteIcon },
        label: t("EntityView_DeleteAction"),
        status: actionButtonStatus,
        onClick: () => setDialogOpen(true),
    };

    const deleteDialogComponent = (
        <DeleteWithConfirmationDialog
            title={t("EntityView_DeleteDialog_Title")}
            open={dialogOpen}
            onConfirm={() => void handleDeleteClick()}
            onCancel={() => setDialogOpen(false)}
            loading={isDeleting}
        >
            {t("EntityView_DeleteDialog_Body")}
        </DeleteWithConfirmationDialog>
    );

    return { deleteAction, deleteDialogComponent };
}
